import styled from '@emotion/styled';
import { Button, Input, notification, Select, Switch, Upload } from 'antd';
import { useEffect, useState } from 'react';
import { FaSave, FaUpload } from 'react-icons/fa';
import { useSelector } from 'react-redux';
import { ConnectedUserEntity } from '../../../../entities/ConnectedUserEntity';
import { DetailTitreEntity } from '../../../../entities/detailTitre.entity';
import { DetenteurEntity } from '../../../../entities/detenteur.entity';
import { ORANGE, PRIMARY, YELLOW } from '../../../../shared/colors';
import { formatNum } from '../../../../shared/constants';
import { StatusPill } from '../../shared/StatusPill';
import { getDetenteurs, operationTitre } from '../network';
import { getSchema } from './getSchema';

const Container = styled.div`
  display: grid;
  gap: 14px;
  color: var(--mt-text);
  font-family: Poppins-R;

  .operation-hero {
    display: grid;
    grid-template-columns: minmax(0, 1fr) auto;
    gap: 16px;
    padding: 16px;
    border: 1px solid rgba(255, 176, 0, 0.22);
    border-radius: 12px;
    background:
      linear-gradient(135deg, rgba(255, 79, 31, 0.14), transparent 42%),
      ${PRIMARY};
    color: #fff7e8;
  }

  .eyebrow {
    display: block;
    color: ${YELLOW};
    font-family: Poppins-B;
    font-size: 11px;
    letter-spacing: 0.08em;
    text-transform: uppercase;
  }

  .operation-hero h3 {
    margin: 5px 0 0;
    color: #fff;
    font-family: Poppins-B;
    font-size: 21px;
  }

  .operation-hero p {
    margin: 6px 0 0;
    color: rgba(255, 247, 232, 0.74);
    font-size: 13px;
  }

  .code-chip {
    align-self: start;
    min-width: 118px;
    padding: 10px;
    border: 1px solid rgba(255, 176, 0, 0.26);
    border-radius: 10px;
    background: rgba(2, 8, 12, 0.42);
  }

  .code-chip span {
    display: block;
    color: rgba(255, 247, 232, 0.68);
    font-size: 10px;
    text-transform: uppercase;
  }

  .code-chip strong {
    display: block;
    margin-top: 4px;
    color: #fff;
    font-family: Poppins-B;
  }

  .summary-grid,
  .form-grid {
    display: grid;
    grid-template-columns: repeat(3, minmax(0, 1fr));
    gap: 10px;
  }

  .summary-card,
  .field-card,
  .upload-card {
    border: 1px solid var(--mt-border);
    border-radius: 10px;
    padding: 12px;
    background: var(--mt-surface);
  }

  .summary-card span,
  .field-card label,
  .upload-card label {
    display: block;
    color: var(--mt-muted);
    font-family: Poppins-B;
    font-size: 10px;
    letter-spacing: 0.04em;
    text-transform: uppercase;
  }

  .summary-card strong {
    display: block;
    margin-top: 5px;
    color: var(--mt-text);
    font-family: Poppins-B;
  }

  .field-card {
    display: grid;
    gap: 8px;
  }

  .field-card.full,
  .upload-card {
    grid-column: 1 / -1;
  }

  .ant-input,
  .ant-select-selector {
    min-height: 38px;
    border-color: var(--mt-border) !important;
    border-radius: 9px !important;
    background: var(--mt-card) !important;
    color: var(--mt-text) !important;
    box-shadow: none !important;
  }

  .scope-row {
    display: flex;
    align-items: center;
    justify-content: space-between;
    gap: 12px;
    border: 1px solid var(--mt-border);
    border-radius: 10px;
    padding: 12px;
    background: var(--mt-surface);
  }

  .scope-row strong {
    color: var(--mt-text);
    font-family: Poppins-B;
  }

  .scope-row span {
    color: var(--mt-muted);
    font-size: 12px;
  }

  .upload-card {
    display: flex;
    align-items: center;
    justify-content: space-between;
    gap: 12px;
  }

  .upload-button {
    border-color: rgba(255, 176, 0, 0.36);
    border-radius: 999px;
    background: var(--mt-card);
    color: var(--mt-warning);
    font-family: Poppins-B;
  }

  .form-actions {
    display: flex;
    justify-content: flex-end;
    gap: 10px;
  }

  .ghost-action,
  .primary-action {
    min-width: 132px;
    height: 40px;
    border-radius: 999px;
    font-family: Poppins-B;
  }

  .ghost-action {
    border-color: var(--mt-border);
    background: var(--mt-surface);
    color: var(--mt-text);
  }

  .primary-action {
    border: 0;
    background: linear-gradient(135deg, ${ORANGE}, ${YELLOW});
    color: #07141c;
    box-shadow: 0 14px 28px rgba(255, 79, 31, 0.2);
  }

  @media (max-width: 780px) {
    .operation-hero,
    .summary-grid,
    .form-grid {
      grid-template-columns: 1fr;
    }
  }
`;

export const OperationTitreForm = ({
  setShowModal,
  detailTitre,
}: {
  setShowModal: (b: boolean) => void;
  detailTitre?: DetailTitreEntity;
}) => {
  const [isLoading, setIsLoading] = useState(false);
  const [full, setFull] = useState(true); // effectuer une opération sur la totalité ou une partie du titre
  const [operation, setOperation] = useState('');
  const [qte, setQte] = useState(detailTitre?.qte || 0);
  const [file, setFile] = useState<any>();
  const [receveur, setReceveur] = useState('');
  const [detenteurs, setDetenteurs] = useState<DetenteurEntity[]>([]);
  const connectedUser: ConnectedUserEntity = useSelector(
    (state: any) => state.userReducer,
  ).user;

  useEffect(() => {
    getDetenteurs(connectedUser.id).then(({ data }) => {
      setDetenteurs(data);
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const needsReceveur = () => {
    if (
      ['mise_pension_titre', 'pret_titre', 'ventre_titre'].includes(operation)
    )
      return true;
    else return false;
  };

  const submitOperation = async () => {
    setIsLoading(true);

    if (detailTitre) {
      const dataToCreate = {
        quantite: qte,
        full,
        operationType: operation,
        // schéma comptable
        schema: getSchema(
          connectedUser,
          detailTitre.detenteur,
          operation as any,
        ),
        // ID du titre
        detailTitreId: detailTitre.id,
        userId: connectedUser.id,
        receveurId: receveur,
        // elements de controle
        eltControl:
          operationValues.find((val) => val.value === operation)?.label || '',
        media: file?.fileList[0]?.originFileObj,
        // info du compte
        code_svt: connectedUser.partenaire.code_referent,
        code_client: detailTitre.detenteur.code_referent,
        code_agence: connectedUser.agence.code,
      };

      console.log(dataToCreate);
      setIsLoading(false);

      await operationTitre(dataToCreate).then(({ data, statusCode }) => {
        if (statusCode?.toString()[0] === '2') {
          notification.success({
            message: 'Succès',
            description: "L'opération sur le titre a été effectue avec succès",
          });
          setShowModal(false);
        } else {
          notification.info({
            message: 'Erreur ' + statusCode.toString(),
            description: data.message,
          });
        }
        setIsLoading(false);
      });
    }
  };

  return (
    <Container>
      <section className='operation-hero'>
        <div>
          <span className='eyebrow'>Opération sur titre</span>
          <h3>{detailTitre?.parentCode || '--'}</h3>
          <p>
            Préparez l'instruction post-marché sur la position sélectionnée.
          </p>
        </div>
        <div className='code-chip'>
          <span>Sous-code</span>
          <strong>{detailTitre?.subCode || '--'}</strong>
        </div>
      </section>

      <section className='summary-grid'>
        <div className='summary-card'>
          <span>Quantité disponible</span>
          <strong>{formatNum(detailTitre?.qte) || '--'}</strong>
        </div>
        <div className='summary-card'>
          <span>Code client</span>
          <strong>{detailTitre?.detenteur.code_referent || '--'}</strong>
        </div>
        <div className='summary-card'>
          <span>État</span>
          <strong>
            <StatusPill tone='neutral'>{detailTitre?.etat || '--'}</StatusPill>
          </strong>
        </div>
      </section>

      <section className='form-grid'>
        <div className='field-card full'>
          <label>Type d'opération</label>
          <Select
            placeholder="Sélectionnez l'opération à effectuer"
            onChange={(val) => setOperation(val)}
          >
            {operationValues.map((op) => (
              <Select.Option key={op.value} value={op.value}>
                {op.label}
              </Select.Option>
            ))}
          </Select>
        </div>
      </section>

      <section className='scope-row'>
        <div>
          <strong>Périmètre de l'opération</strong>
          <span>
            {full
              ? 'La totalité de la position sera utilisée.'
              : 'Indiquez la quantité partielle à traiter.'}
          </span>
        </div>
        <Switch
          defaultChecked
          onChange={(checked: boolean) => setFull(checked)}
        />
      </section>

      {!full && (
        <section className='form-grid'>
          <div className='field-card full'>
            <label>Quantité à traiter</label>
            <Input
              placeholder='Entrez la quantité'
              type='number'
              min={0}
              max={detailTitre?.qte}
              onChange={(e) => setQte(Number(e.target.value))}
            />
          </div>
        </section>
      )}

      {needsReceveur() && (
        <section className='form-grid'>
          <div className='field-card full'>
          <label>Receveur</label>
          <Select
            placeholder='Sélectionnez le receveur'
            onChange={(val) => setReceveur(val)}
          >
            {detenteurs
              .filter(
                (det) =>
                  det.code_referent !== detailTitre?.detenteur.code_referent,
              )
              .map((det) => (
                <Select.Option key={det.id} value={det.id}>
                  {det.code_referent} - {det.nom} {det.prenom}
                </Select.Option>
              ))}
          </Select>
          </div>
        </section>
      )}

      <section className='upload-card'>
        <div>
          <label>Pièce justificative</label>
          <span className='muted'>Facultatif, utile pour audit et contrôle.</span>
        </div>
        <Upload
          name='piece'
          onChange={(fichier) => {
            setFile(fichier as any);
          }}
        >
          <Button
            className='upload-button'
            icon={<FaUpload style={{ marginRight: 10 }} />}
          >
            Sélectionner
          </Button>
        </Upload>
      </section>

      <div className='form-actions'>
        <Button className='ghost-action' onClick={() => setShowModal(false)}>
          Annuler
        </Button>
        <Button
          loading={isLoading}
          icon={<FaSave style={{ marginBottom: -1.8, marginRight: 4 }} />}
          className='primary-action'
          onClick={submitOperation}
        >
          Exécuter l'opération
        </Button>
      </div>
    </Container>
  );
};

const operationValues = [
  {
    label: 'Livraison de titre',
    value: 'liv_titre',
  },
  {
    label: 'Vente de titre',
    value: 'ventre_titre',
  },
  {
    label: 'Mise en pension',
    value: 'mise_pension_titre',
  },
  {
    label: 'Restitution titre en pension livrée',
    value: 'restitu_pension_titre',
  },
  {
    label: 'Défaut de restitution titre en pension livrée',
    value: 'defaut_pension_titre',
  },
  {
    label: 'Prêt de titre',
    value: 'pret_titre',
  },
  {
    label: 'Restitution prêt de titre',
    value: 'restitu_pret_titre',
  },
  {
    label: 'Défaut prêt de titre',
    value: 'defaut_pret_titre',
  },
  {
    label: 'Transfert de titres reçus à la CRCT',
    value: 'transf_titre_CRCT',
  },
  {
    label: 'Transfert par la CRCT vers autre SVT',
    value: 'transf_titre_SVT',
  },
  {
    label: 'Non remboursement de titre échus',
    value: 'non_remb_titre',
  },
  {
    label: 'Remboursement de titre échus',
    value: 'remb_titre',
  },
  {
    label: 'Litige',
    value: 'litige_titre',
  },
  {
    label: 'Fin du litige',
    value: 'fin_litige_titre',
  },
];
